import React, { useState, useRef, ChangeEvent, KeyboardEvent, useEffect } from "react"
import { TextField } from "@mui/material"
import styles from './OTP.module.css'
import { useTranslations } from "next-intl"

interface OTPProps {
  numberOfInputs?: number
  numeric?: boolean
  onChange?: (value: string) => void // Callback to return the complete OTP
  error?: boolean
}

const DEFAULT_NUMBER_OF_INPUTS = 4

export default function OTP ({ numberOfInputs = DEFAULT_NUMBER_OF_INPUTS, numeric = true, onChange, error = false }: OTPProps) {
  const [values, setValues] = useState<string[]>(Array(numberOfInputs).fill(""))
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const t = useTranslations('ForgotPassword')

  /**
   * Handles OTP input changes
   * 
   * @param index - The index of the input field that triggered the change
   * @param event - The change event from the input field
   */
  const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value

    if (!numeric || /^\d$/.test(val)) {
      const newValues = [...values]
      newValues[index] = val
      
      setValues(newValues)
      if (onChange) onChange(newValues.join(""))

      // Move focus to the next input if there is a value and it's not the last input
      if (val && index < numberOfInputs - 1) {
        const nextInput = inputsRef.current[index + 1]
        if(nextInput) nextInput.focus()
      }
    }
  }

  /**
   * Handles keyboard events for each input field.
   * If the current field has a value, it clears it. If it's already empty, it moves focus to the previous field.
   * 
   * @param index - The index of the current input field
   * @param event - The keyboard event triggered by the user
   */
  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      const newValues = [...values]

      if (newValues[index]) {
        newValues[index] = ''
        setValues(newValues)
        if (onChange) onChange(newValues.join(''))
      } else if (index > 0) {
        const prevInput = inputsRef.current[index - 1]
        if (prevInput) {
          window.setTimeout(() => {
            prevInput.focus()
            prevInput.select()
          }, 0)
        }
      }
    }
  }

  return (
    <div className={styles.main} data-testid="OTP">
      <div className={styles.contentWrapper}>
        <div className={styles.otpWrapper}>
          {values.map((value, index) => (
            <TextField
              key={index}
              value={value}
              className={`bodyTextLarge ${styles.fieldNumber}`}
              autoFocus={index === 0}
              onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
              onInput={(e: ChangeEvent<HTMLInputElement>) => handleChange(index, e)}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
              inputRef={(el) => (inputsRef.current[index] = el)}
              variant="outlined"
              size="medium"
              data-testid="FieldNumber"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid white',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(var(--surface))',
                  backdropFilter: 'blur(15px)',
                  "@media screen and (min-width: 768px)": {
                    borderRadis: '16px'
                  }
                },
                "&not:(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                  borderColor: "white !important",
                },
              }}
              error={error}
              slotProps={{
                htmlInput: {
                  maxLength: 1,
                  style: { textAlign: "center" },
                },
                input:{
                  className: `${styles.inputField}`
                }
              }}
            />
          ))}
        </div>

        <p data-testid="OTP_validateError" className={`bodyTextMedium ${styles.validateCodeError}`}>{error ? t('messages.server.step2ValidateCodeError') : ' '}</p>
      </div>
    </div>
  )
}
